import * as T from 'three';
import { Component } from './live-codec.js';
import { SourceRandom } from './weapon-events.js';

/** Small client simulation: server death identity/flags/impulse, studio joints,
 * fixed 60 Hz gravity + distance constraints and BSP traces. No physics engine. */
export class RagdollMotion {
    constructor(entity, time, entities) {
        this.entity = entity; this.state = entity.state.components[Component.Ragdoll];
        this.lastTime = time; this.points = []; this.age = Math.max(0, (time - this.state.startTime) / 1000);
        const source = entities.get(`${this.state.playerIndex}:${this.state.playerGeneration}`);
        if (source?.rig && entity.rig) {
            const names = new Map(source.rig.bones.map(b => [b.name.toLowerCase(), b]));
            for (const bone of entity.rig.bones) {
                const from = names.get(bone.name.toLowerCase());
                if (from) { bone.position.copy(from.position); bone.quaternion.copy(from.quaternion); }
            }
        }
        entity.object.updateWorldMatrix(true, true);
        this.inverse = new T.Matrix4(); this.world = new T.Matrix4(); this.position = new T.Vector3(); this.quaternion = new T.Quaternion();
        const impulse = new T.Vector3().fromArray(this.state.force).multiplyScalar(1 / 80).clampLength(0, 400);
        this.velocity = new T.Vector3().fromArray(this.state.velocity).add(impulse).clampLength(0, 600);
        this.bodyPosition = entity.object.position.clone(); this.bodyRotation = entity.object.quaternion.clone();
        if (entity.rig) {
            const names = entity.rig.data.physicsBones?.map(name => name.toLowerCase());
            this.points = entity.rig.bones.flatMap((bone, i) => names?.length && !names.includes(bone.name.toLowerCase()) ? [] : [{ bone, index: i, parent: -1,
                position: bone.getWorldPosition(new T.Vector3()), rotation: bone.getWorldQuaternion(new T.Quaternion()), velocity: this.velocity.clone() }]);
            for (const point of this.points) {
                let index = entity.rig.data.bones[point.index].parent;
                while (index >= 0) {
                    const parent = this.points.findIndex(p => p.index === index);
                    if (parent >= 0) { point.parent = parent; break; }
                    index = entity.rig.data.bones[index].parent;
                }
            }
            const origin = this.points[0]?.position ?? this.bodyPosition;
            // A small deterministic initial spin breaks perfectly vertical rest
            // poses, matching TF's client-created angular impulse (~120 deg/s).
            const spin = new T.Vector3(0, 2, .25);
            for (const point of this.points) {
                point.velocity.add(new T.Vector3().crossVectors(spin, point.position.clone().sub(origin)));
                const parent = this.points[point.parent];
                point.length = parent ? point.position.distanceTo(parent.position) : 0;
                point.child = this.points.findIndex(p => p.parent === this.points.indexOf(point) && p.position.distanceTo(point.position) > .1);
                point.direction = point.child >= 0 ? this.points[point.child].position.clone().sub(point.position).normalize() : null;
            }
        }
    }
    stepPoint(point, dt, trace) {
        const start = point.position.toArray(); point.velocity.z -= 800 * dt;
        point.velocity.multiplyScalar(.99); const end = point.position.clone().addScaledVector(point.velocity, dt);
        const hit = trace?.(start, end.toArray()) ?? { fraction: 1 };
        if (hit.fraction < 1) {
            point.position.lerp(end, Math.max(0, hit.fraction));
            const normal = new T.Vector3().fromArray(hit.normal ?? [0, 0, 1]);
            point.position.addScaledVector(normal, .5);
            const speed = point.velocity.dot(normal);
            if (speed < 0) point.velocity.addScaledVector(normal, -speed * 1.15);
            point.velocity.multiplyScalar(.65);
        } else point.position.copy(end);
    }
    update(time, trace) {
        const elapsed = Math.max(0, Math.min(100, time - this.lastTime)); this.lastTime = time;
        const steps = Math.min(6, Math.ceil(elapsed / (1000 / 60))), dt = steps ? elapsed / steps / 1000 : 0;
        this.age = Math.max(0, (time - this.state.startTime) / 1000);
        if (this.age > 15) return;
        for (let step = 0; step < steps; step++) {
            if (!this.points.length) {
                const point = { position: this.bodyPosition, velocity: this.velocity }; this.stepPoint(point, dt, trace);
            }
            for (const point of this.points) this.stepPoint(point, dt, trace);
            for (let pass = 0; pass < 3; pass++) for (const point of this.points) {
                const parent = this.points[point.parent]; if (!parent || point.length < .001) continue;
                const delta = point.position.clone().sub(parent.position), length = delta.length();
                if (length > .001) { delta.multiplyScalar((length - point.length) / length * .5); point.position.sub(delta); parent.position.add(delta); }
            }
        }
        const object = this.entity.object;
        if (!this.points.length) { object.position.copy(this.bodyPosition); object.quaternion.copy(this.bodyRotation).multiply(new T.Quaternion().setFromAxisAngle(new T.Vector3(0,1,0), Math.min(Math.PI / 2, this.age * 2))); return; }
        object.updateWorldMatrix(true, true);
        // Model table order is not necessarily topological.
        const visited = new Set();
        const pose = index => {
            if (visited.has(index)) return; visited.add(index);
            const point = this.points[index]; if (point.parent >= 0) pose(point.parent);
            this.quaternion.copy(point.rotation);
            if (point.direction && point.child >= 0) {
                const direction = this.points[point.child].position.clone().sub(point.position).normalize();
                this.quaternion.premultiply(new T.Quaternion().setFromUnitVectors(point.direction, direction));
            }
            this.world.compose(point.position, this.quaternion, new T.Vector3(1,1,1));
            this.inverse.copy(point.bone.parent.matrixWorld).invert().multiply(this.world);
            this.inverse.decompose(point.bone.position, point.bone.quaternion, point.bone.scale);
            point.bone.updateWorldMatrix(false, true);
        };
        for (let i = 0; i < this.points.length; i++) pose(i);
    }
}

export function gibVelocity(state, index, generation) {
    const random = new SourceRandom(generation * 31 + index), direction = new T.Vector3().fromArray(state.velocity).add(new T.Vector3().fromArray(state.force).multiplyScalar(1 / 80));
    direction.z += 100;
    if (direction.lengthSq() < 1) direction.set(0, 0, 1);
    direction.normalize().multiplyScalar(300);
    direction.add(new T.Vector3(random.range(-100,100),random.range(-100,100),random.range(0,100))).clampLength(0,400);
    return direction.toArray();
}
